import React, {useState, useEffect, useRef} from 'react';
import useDebounce from './useDebounce';

let autocompleteInstanceCount = 0;

// Suggestions open only in response to the user typing in the box (the usual combobox practice). A value the page
// sets itself - restored from saved search state when navigating back, or filled in by picking a suggestion - is
// shown but not looked up, results only open while the box has focus, and submitting a search closes them.

function Autocomplete({
  name,
  placeholder,
  value,
  onChange,
  onKeyDown,
  fetchSuggestions,
  getSuggestionKey,
  renderSuggestion,
  onSelect,
  minLength = 3,
  debounceMs = 300
}) {
  const listboxId = useRef(`autocomplete-listbox-${++autocompleteInstanceCount}`).current;
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const debouncedValue = useDebounce(value, debounceMs);
  const requestIdRef = useRef(0);
  // True until the user types: the starting value (restored state) and picked suggestions aren't looked up
  const suppressFetchRef = useRef(true);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const hasFocus = () => document.activeElement === inputRef.current;

  useEffect(() => {
    if (suppressFetchRef.current) {
      return;
    }
    if (!debouncedValue || debouncedValue.trim().length < minLength) {
      setSuggestions([]);
      setIsOpen(false);
      setFetchError(false);
      setIsFetching(false);
      return;
    }

    let cancelled = false;
    const requestId = ++requestIdRef.current;
    setIsFetching(true);

    fetchSuggestions(debouncedValue.trim())
      .then(async response => {
        if (cancelled || requestId !== requestIdRef.current) return;
        if (!response.ok) {
          setFetchError(true);
          setSuggestions([]);
          setIsOpen(hasFocus());
          return;
        }
        const data = await response.json();
        if (cancelled || requestId !== requestIdRef.current) return;
        setFetchError(false);
        setSuggestions(data);
        setIsOpen(hasFocus());
        setHighlightedIndex(-1);
      })
      .catch(() => {
        if (cancelled || requestId !== requestIdRef.current) return;
        setFetchError(true);
        setSuggestions([]);
        setIsOpen(hasFocus());
      })
      .finally(() => {
        if (!cancelled && requestId === requestIdRef.current) setIsFetching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedValue, minLength, fetchSuggestions]);

  useEffect(() => {
    const handleDocClick = event => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => document.removeEventListener('mousedown', handleDocClick);
  }, []);

  const handleSelect = item => {
    suppressFetchRef.current = true;
    setIsOpen(false);
    onSelect(item);
  };

  const handleKeyDown = event => {
    if (isOpen && suggestions.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        setHighlightedIndex(i => (i + 1) % suggestions.length);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        setHighlightedIndex(i => (i - 1 + suggestions.length) % suggestions.length);
        return;
      }
      if (event.key === 'Enter' && highlightedIndex >= 0) {
        event.preventDefault();
        handleSelect(suggestions[highlightedIndex]);
        return;
      }
      if (event.key === 'Escape') {
        setIsOpen(false);
        setHighlightedIndex(-1);
        return;
      }
    }
    if (event.key === 'Enter') {
      // The page runs a full search; don't let a pending or in-flight lookup reopen the list over its results
      suppressFetchRef.current = true;
      requestIdRef.current++;
      setIsFetching(false);
      setIsOpen(false);
      setHighlightedIndex(-1);
    }
    onKeyDown(event);
  };

  return (
    <div className="search-bar" ref={containerRef}>
      <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none">
        <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2"/>
        <path d="M21 21l-4.3-4.3" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
      </svg>
      <input
        ref={inputRef}
        name={name}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={event => {
          suppressFetchRef.current = false;
          onChange(event.target.value);
        }}
        onKeyDown={handleKeyDown}
        onFocus={() => {
          if (suggestions.length > 0) setIsOpen(true);
        }}
        autoComplete="off"
        role="combobox"
        aria-expanded={isOpen}
        aria-autocomplete="list"
        aria-controls={listboxId}
      />
      {isFetching && <span className="spinner autocomplete-spinner"/>}
      {isOpen &&
        <div id={listboxId} role="listbox" className="autocomplete-panel result-list">
          {fetchError &&
            <div className="autocomplete-empty">Couldn't load suggestions</div>
          }
          {!fetchError && suggestions.length === 0 &&
            <div className="autocomplete-empty">No matches</div>
          }
          {!fetchError && suggestions.map((item, index) => (
            <div
              key={getSuggestionKey(item)}
              className={`result-row is-link autocomplete-row${index === highlightedIndex ? ' is-highlighted' : ''}`}
              onMouseDown={event => {
                event.preventDefault();
                handleSelect(item);
              }}
              onMouseEnter={() => setHighlightedIndex(index)}
            >
              <span className="result-row-title">{renderSuggestion(item)}</span>
            </div>
          ))}
        </div>
      }
    </div>
  );
}

export default Autocomplete;
